/* eslint-disable no-cond-assign, prefer-destructuring */

/* Vague TODO - see if the unused variables are dead code */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { NotApplicableCell, PercentageCell } from '../../../../views/components/cells';
import { deepClone } from '../../../../utils/objects';
import { formatCurrency, formatLocalizedString } from '../../../../utils/i18n';
import DonutData from '@/types/donut-data';
import FundAllocation from '@/types/fund-allocation';
import PercentGrowth from '@/types/percent-growth';
import GrowthTypes from '@/types/growth-types';
import LocalizedString from '@/types/localized-string';

/**
 * @class CountryAllocationDAO Data Access Object
 */
export default class PortfolioFundHoldingsDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new PortfolioFundHoldingsDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['DEFAULT'];
  }

  protected build(): void {
    let fieldAsterisks: string[] = [];
    let showMER: boolean = true;
    let growthPeriods: Array<GrowthTypes> = ['1Y', '3Y', '5Y', '10Y', 'INC'];
    let showAssetClass = false;
    let showAmount = false;

    const daoConfig: ProposalNS.PortfolioHoldingsConfig = this.proposalElement.DAO as ProposalNS.PortfolioHoldingsConfig;

    if (daoConfig.extraColumnsDAOOptions !== undefined && daoConfig.extraColumnsDAOOptions.columns.indexOf('ASSET_CLASS') !== -1) {
      showAssetClass = true;
    }

    if (daoConfig.extraColumnsDAOOptions !== undefined && daoConfig.extraColumnsDAOOptions.columns.indexOf('AMOUNT') !== -1) {
      showAmount = true;
    }

    if (daoConfig.fieldAsterisks !== undefined) {
      fieldAsterisks = daoConfig.fieldAsterisks || [];
    }

    if (daoConfig.showMER !== undefined) {
      showMER = daoConfig.showMER;
    }

    if (daoConfig.growthPeriods !== undefined) {
      growthPeriods = daoConfig.growthPeriods;
    }

    if (this.customFieldNames != null) {
      for (const name of this.customFieldNames.names) {
        this.addFieldName(this.formatLocalizedString(name), ProposalStyleClassNames.ProposalTableContent);
      }
    }

    let itemDAO: ComparisonItemDAO | null = this.nextItem();
    while (itemDAO !== null) {
      const { response, disclaimerKeys } = DataPoints.getFundHoldingsByAssetGroup(itemDAO.item);

      const hasData = Object.values(response).some(assetGroup => assetGroup.length > 0);

      if (hasData) {
        this.addDisclaimerKeys(disclaimerKeys, itemDAO.item);
      }

      for (const [key, assetGroup] of Object.entries(response)) {
        let total = 0;
        let rowIndex = 0;

        const funds = assetGroup.slice();

        // Sub-group items by their classification
        funds.sort((a, b) => {
          const formatted = [formatLocalizedString(a.classification), formatLocalizedString(b.classification)];
          return formatted[0].localeCompare(formatted[1]);
        });

        for (const fund of funds) {
          let index = 0;

          const classes = [ProposalStyleClassNames.ProposalTableContent];
          classes.push(rowIndex % 2 ? ProposalStyleClassNames.ProposalOdd : ProposalStyleClassNames.ProposalEven);

          this.addField(fund.name, classes);

          if (showAssetClass) {
            this.addStringValue(itemDAO, fund.classification, index++, classes);
          }

          this.addPercentValue(itemDAO, fund.weight, index++, 1, classes);

          if (showAmount) {
            this.addDollarsValue(itemDAO, itemDAO.item.size != null ? 0.01 * fund.weight * itemDAO.item.size : undefined, index++, 2, [
              ...classes,
              `growth-${key}`,
            ]);
          }

          total += fund.weight;

          index = this.addPercentGrowths(itemDAO, fund, index, growthPeriods, classes);

          if (showMER) {
            this.addMER(itemDAO, fund, index++, classes);
          }

          rowIndex++;
        }

        // do not show the total if there is no holdings
        if (rowIndex > 0) {
          let fieldName = this.formatMessage(`Proposal.Totals.${key}`);
          if (fieldAsterisks.includes(key)) {
            fieldName = `*${fieldName}`;
          }

          this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableTotal);
          const field: ProposalFieldDAO = new ProposalFieldDAO(key, '', ProposalStyleClassNames.ProposalTableTotal);
          this.padItem(itemDAO, field);

          // Add the total
          const totalField: ProposalFieldDAO = itemDAO.getFieldDAOs(0)[itemDAO.getFieldDAOs(0).length - 1];
          totalField.value = this.formatPercentage(total, 1);

          if (showAmount) {
            const totalFieldValue: ProposalFieldDAO = itemDAO.getFieldDAOs(1)[itemDAO.getFieldDAOs(1).length - 1];
            totalFieldValue.value = itemDAO.item.size != null ? formatCurrency(0.01 * total * itemDAO.item.size, 2, undefined, false, this.locale) : null;
          }
        }
      }

      // // Add the grand Total
      const grandTotalLabel = this.formatMessage('Proposal.Totals.Portfolio');
      this.addFieldName(grandTotalLabel, ProposalStyleClassNames.ProposalTableTotal);
      let grandTotalField: ProposalFieldDAO = new ProposalFieldDAO(grandTotalLabel, '', ProposalStyleClassNames.ProposalTableTotal);
      this.padItem(itemDAO, grandTotalField);
      grandTotalField = itemDAO.getFieldDAOs(0)[itemDAO.getFieldDAOs(0).length - 1];
      grandTotalField.value = 100;
      grandTotalField.cellType = PercentageCell;

      if (showAmount) {
        grandTotalField = itemDAO.getFieldDAOs(1)[itemDAO.getFieldDAOs(1).length - 1];
        grandTotalField.value = itemDAO.item.size != null ? formatCurrency(itemDAO.item.size, 2, undefined, false, this.locale) : null;
      }

      itemDAO = this.nextItem();
    }
  }

  // private Methods
  addMER(itemDAO: ComparisonItemDAO, fund: FundAllocation, index: number, classes?: string[]) {
    const rawValue = fund.mer;
    if (rawValue != null) {
      this.addPercentValue(itemDAO, rawValue, index, 2, classes);
    } else {
      this.addNACell(itemDAO, index, classes);
    }
  }

  addDollarsValue(itemDAO: ComparisonItemDAO, value?: number, index?: number, decimals: number = 2, classes: string[] = []) {
    let strValue = '';
    if (value != null) {
      strValue = formatCurrency(value, decimals, undefined, false, this.locale);
    }

    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', strValue, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  addPercentGrowths(itemDAO: ComparisonItemDAO, fund: FundAllocation, index: number, growthPeriods: GrowthTypes[], classes: string[] = []): number {
    const percentGrowth: Partial<PercentGrowth> = fund.percentageGrowth;
    let localIndex = index;

    for (const key of growthPeriods) {
      const rawGrowth = percentGrowth[key];
      if (rawGrowth != null) {
        this.addGrowthValue(itemDAO, rawGrowth, localIndex, [...classes, `growth-${key}`]);
      } else {
        this.addNACell(itemDAO, localIndex, classes);
      }
      localIndex++;
    }

    return localIndex;
  }

  addField(rawString: LocalizedString | undefined, classes: string[] = []) {
    if (rawString !== undefined) {
      const str: string = formatLocalizedString(rawString, this.locale);
      this.addFieldName(str, this.mergeClasses(classes));
    }
  }

  addStringValue(itemDAO: ComparisonItemDAO, rawString: LocalizedString | undefined, index: number, classes: string[] = []) {
    let str: string = '';
    if (rawString !== undefined) {
      str = formatLocalizedString(rawString, this.locale);
    }
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO(str, str, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addPercentValue(itemDAO: ComparisonItemDAO, value: number, index: number, decimals: number = 2, classes: string[] = []) {
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', this.formatPercentage(value, decimals), this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addGrowthValue(itemDAO: ComparisonItemDAO, value: number, index: number, classes: string[] = []) {
    if (value > 0) {
      classes.push('positive');
    } else if (value < 0) {
      classes.push('negative');
    }

    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', this.formatPercentage(value, 2), this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addNACell(itemDAO: ComparisonItemDAO, index: number, classes: string[] = []) {
    const naField: ProposalFieldDAO = new ProposalFieldDAO('', '', this.mergeClasses(classes.concat(ProposalStyleClassNames.ProposalNAValue)));
    naField.cellType = NotApplicableCell;
    this.addFieldDAO(itemDAO, naField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  padItem(itemDAO: ComparisonItemDAO, field: ProposalFieldDAO) {
    this.addFieldDAO(itemDAO, field, 0);
    if (this.customColumnNames !== null) {
      for (let index: number = 1; index < this.customColumnNames.columns.length; index++) {
        this.addFieldDAO(itemDAO, deepClone(field), index);
      }
    }
  }

  mergeClasses = (classes: string[]) => classes.join(' ');

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }
    return out;
  }
}
